import supabase from '../lib/supabase';

/**
 * Check if a user has an active subscription
 * This implements the hybrid approach that checks for:
 * 1. Admin status
 * 2. Lifetime access
 * 3. Promotional subscription
 * 4. Regular Stripe subscription
 * 
 * @param {string} userId - The user ID to check
 * @returns {Promise<Object>} - Object with subscription status and details
 */
export const checkSubscription = async (userId) => {
  if (!userId) {
    return { isSubscribed: false, plan: 'free' };
  }
  
  try {
    
    // First check for special cases in profiles
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('is_admin, has_lifetime_access, promo_subscription_end_date')
      .eq('id', userId)
      .single();
      
    if (profileError) {
      return { 
        isSubscribed: false, 
        plan: 'free',
        type: 'error',
        isAdmin: false
      };
    }
    
    // Check if the user is an admin
    if (profile?.is_admin) {
      return { 
        isSubscribed: true, 
        plan: 'pro',
        type: 'admin',
        isAdmin: true
      };
    }
    
    // Check for lifetime access
    if (profile?.has_lifetime_access) {
      return { 
        isSubscribed: true, 
        plan: 'pro',
        type: 'lifetime',
        isAdmin: false
      };
    }
    
    // Check for promotional subscription
    if (profile?.promo_subscription_end_date) {
      const endDate = new Date(profile.promo_subscription_end_date);
      const now = new Date();
      
      if (endDate > now) {
        return { 
          isSubscribed: true, 
          plan: 'pro',
          type: 'promotional',
          endDate: endDate.toISOString(),
          isAdmin: false
        };
      } else {
      }
    }
    
    // Now check regular subscriptions table
    try {
      const { data: subscription, error: subscriptionError } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .eq('status', 'active')
        .maybeSingle();
        
      if (subscriptionError) {
        // Continue to beta access fallback
      } else if (subscription) {
        // If it's a custom subscription without Stripe
        if (subscription.is_custom) {
          if (!subscription.end_date || new Date(subscription.end_date) > new Date()) {
            return { 
              isSubscribed: true, 
              plan: subscription.plan_id,
              type: 'custom',
              endDate: subscription.end_date,
              isAdmin: false
            };
          }
        }
        
        // If it's a Stripe subscription
        if (subscription.stripe_subscription_id) {
          return { 
            isSubscribed: true, 
            plan: subscription.plan_id,
            type: 'stripe',
            stripeId: subscription.stripe_subscription_id,
            isAdmin: false
          };
        }
      }
    } catch (error) {
      // Continue to beta access fallback
    }
    
    // No active subscription found
    return { 
      isSubscribed: false, 
      plan: 'free',
      type: 'none',
      isAdmin: false
    };
  } catch (error) {
    return { isSubscribed: false, error };
  }
};

/**
 * Check if a user has admin access
 * 
 * @param {string} userId - The user ID to check
 * @returns {Promise<boolean>} - True if user has admin access
 */
export const checkAdminAccess = async (userId) => {
  if (!userId) {
    return false;
  }
  
  try {
    // First, directly check if the user exists in profiles
    const { data: profileExists, error: profileExistsError } = await supabase
      .from('profiles')
      .select('id')
      .eq('id', userId);
      
    
    if (profileExistsError) {
    }
    
    if (!profileExists || profileExists.length === 0) {
      
      // Try to create a profile for the user
      const { error: insertError } = await supabase
        .from('profiles')
        .insert({ id: userId, is_admin: false, has_lifetime_access: false })
        .single();
        
      if (insertError) {
      } else {
      }
    }
    
    // Now check for admin status
    const { data, error } = await supabase
      .from('profiles')
      .select('is_admin')
      .eq('id', userId)
      .single();
    
      
    if (error) {
      return false;
    }
    
    if (!data) {
      return false;
    }
    
    // Check if the user is an admin based on the database value
    
    // Check if the user is an admin based on the database value
    const isAdmin = data.is_admin === true;
    
    
    return isAdmin;
  } catch (error) {
    return false;
  }
};

const subscriptionUtils = {
  checkSubscription,
  checkAdminAccess
};

export default subscriptionUtils;
